import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';

const FullScreenEditor = ({ 
  isOpen, 
  onClose, 
  promptData, 
  onSave,
  isSaving,
  getCurrentPromptValue,
  getCurrentSchemaValue,
  onPromptChange,
  onSchemaChange,
  hasChanges
}) => {
  // Get current values from parent
  const promptValue = getCurrentPromptValue ? getCurrentPromptValue() : '';
  const schemaValue = getCurrentSchemaValue ? getCurrentSchemaValue() : '';

  // Handle prompt changes - pass through to parent
  const handlePromptChange = (value) => {
    if (onPromptChange) {
      onPromptChange(value || '');
    }
  };

  // Handle schema changes - pass through to parent
  const handleSchemaChange = (value) => {
    if (onSchemaChange) {
      onSchemaChange(value || '');
    }
  };

  // Handle save
  const handleSave = () => {
    if (onSave) {
      onSave(promptValue, schemaValue);
    }
  };

  // Handle close with confirmation if there are unsaved changes
  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      // Escape to close
      if (e.key === 'Escape') {
        handleClose();
      }
      
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasChanges) {
          handleSave();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasChanges]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="w-full h-full bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-800">
              {promptData.promptNameID} (id: {promptData.prompt_id})
            </h2>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Unsaved changes indicator */}
            {hasChanges && (
              <span className="text-orange-600 text-sm">Unsaved changes</span>
            )}
            
            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                hasChanges && !isSaving
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        {/* Editor Content */}
        <div className="flex-1 flex min-h-0 p-4 gap-4">
          {/* Prompt Editor - Left Side */}
          <div className="flex-1 flex flex-col border border-gray-200 rounded-lg overflow-hidden p-2">
            <div className="flex-1">
              <Editor
                height="100%"
                defaultLanguage="markdown"
                value={promptValue}
                onChange={handlePromptChange}
                options={{
                  readOnly: false,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  fontSize: 14,
                  lineNumbers: 'off',
                  folding: false,
                  glyphMargin: false,
                  lineDecorationsWidth: 0,
                  lineNumbersMinChars: 0,
                  renderLineHighlight: 'none',
                  automaticLayout: true,
                  'breadcrumbs.enabled': false
                }}
                theme="vs"
              />
            </div>
          </div>

          {/* Schema Editor - Right Side */}
          <div className="flex-1 flex flex-col border border-gray-200 rounded-lg overflow-hidden p-2">
            <div className="flex-1">
              <Editor
                height="100%"
                defaultLanguage="json"
                value={schemaValue}
                onChange={handleSchemaChange}
                options={{
                  readOnly: false,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  fontSize: 14,
                  lineNumbers: 'on',
                  folding: false,
                  glyphMargin: false,
                  renderLineHighlight: 'none',
                  automaticLayout: true,
                  tabSize: 2,
                  insertSpaces: true,
                  'breadcrumbs.enabled': false
                }}
                theme="vs"
              />
            </div>
          </div>
        </div>

        {/* Footer with keyboard shortcuts */}
        <div className="p-2 bg-gray-50 border-t text-xs text-gray-500 text-center">
          <span className="mr-4">Press <kbd className="px-1 py-0.5 bg-gray-200 rounded">Esc</kbd> to close</span>
          <span>Press <kbd className="px-1 py-0.5 bg-gray-200 rounded">Ctrl+S</kbd> to save</span>
        </div>
      </div>
    </div>
  );
};

export default FullScreenEditor;
